/**
 * utentiPerTenant — fonte UNICA per rispondere a due domande gemelle:
 *
 *   (1) «QUALI PERSONE operano per questa azienda?»       → `appartieneAlTenant`
 *   (2) «CHE RUOLO ha questa persona IN questa azienda?»  → `risolviRuoloSulTenant`
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PERCHÉ ESISTE — `azienda_id` non è più la risposta
 * ─────────────────────────────────────────────────────────────────────────────
 * Prima del multi-tenant, «utente dell'azienda T» era esattamente
 * `app_users.azienda_id == T`. Con le membership è FALSO in entrambe le direzioni:
 *
 *   - Andrea Atzeni ha `azienda_id = Fortezza` e una membership ATTIVA su Vivisolar.
 *     Per Vivisolar è un collaboratore a tutti gli effetti, ma la query per
 *     `azienda_id` non lo trova → l'admin di Vivisolar non ha nemmeno una scheda
 *     da cui configurarlo. È il bug che questo modulo esiste per chiudere.
 *   - Il suo RUOLO su Vivisolar è `commerciale_esterno`, mentre il campo top-level
 *     `ruolo` dice `manager` (che è il suo ruolo IN FORTEZZA). Mostrare il ruolo
 *     top-level nella lista di Vivisolar non è solo impreciso: su
 *     `gerolamo.grosso@powerservicesrl.eu` (top-level `admin` in Power Service,
 *     `tecnico` in Vivisolar) mostrerebbe «Admin» a un'azienda in cui admin non è.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PERCHÉ SERVE L'UNIONE DI DUE QUERY, E NON UNA SOLA
 * ─────────────────────────────────────────────────────────────────────────────
 * `membership_tenant_ids` è un campo DERIVATO (lo scrive `setUserClaims`) che
 * contiene i tenant delle membership **attive**. Da solo NON basta:
 *
 *   - una membership SOSPESA (`attiva: false`) non entra nel campo derivato, che
 *     resta `[]`. Misurato sui dati reali il 2026-07-20: **6 utenti** si trovano in
 *     questo stato (2 Vivisolar, 3 Power Service, 1 Fortezza), tutti
 *     `status: 'disabilitato'`. Sono dipendenti disattivati della loro stessa
 *     azienda: oggi compaiono in lista e l'admin li può riattivare. Con il solo
 *     `array-contains` SPARIREBBERO — una regressione silenziosa su gente reale.
 *   - il campo è scritto in modo ASINCRONO da un trigger: fra la scrittura della
 *     membership e l'allineamento del derivato esiste una finestra in cui è stantìo.
 *
 * E la query per `azienda_id` da sola perde ovviamente i collaboratori. Quindi:
 *
 *     UNIONE( azienda_id == T , membership_tenant_ids array-contains T )
 *     → dedup per uid → filtro di sicurezza `appartieneAlTenant`
 *
 * Il filtro finale non è ridondanza difensiva: le due query leggono `azienda_id` e
 * un campo DERIVATO, mentre la verità è `tenant_memberships[]`. Quando il derivato è
 * stantìo (collaboratore appena sospeso, trigger non ancora girato) la query lo
 * restituisce ancora: il filtro lo scarta leggendo il campo canonico. È lo stesso
 * principio di «SSOT nemico numero uno»: si decide sulla fonte, non sulla copia.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NESSUN INDICE FIRESTORE NUOVO — ed è una scelta, non un caso
 * ─────────────────────────────────────────────────────────────────────────────
 * `unisciUtentiPerTenant` è pensata per essere alimentata da due query a VINCOLO
 * SINGOLO (`azienda_id ==` e `membership_tenant_ids array-contains`), entrambe
 * servite dagli indici automatici a campo singolo. Filtro di stato e ordinamento
 * si fanno in memoria — non per pigrizia, ma perché sono OBBLIGATORI in memoria:
 * Firestore non sa ordinare ATTRAVERSO due query distinte, quindi la lista fusa va
 * comunque riordinata lato client. Aggiungere `status`/`orderBy` dentro la seconda
 * query richiederebbe un indice composito
 * (`membership_tenant_ids CONTAINS + status ASC + cognome ASC`) che non darebbe
 * alcun ordine utilizzabile. Vedi `UserService.queryUsersOnTenant`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ISOLAMENTO FRA AZIENDE (D3) — questo modulo non risale MAI al top-level
 * ─────────────────────────────────────────────────────────────────────────────
 * `risolviRuoloSulTenant` usa il campo top-level SOLO quando il tenant richiesto è
 * il tenant PRIMARIO dell'utente: lì `ruolo`/`roles` descrivono quella stessa
 * azienda. Su un tenant SECONDARIO il top-level è il ruolo di UN'ALTRA azienda, e
 * usarlo sarebbe un'escalation cross-tenant. Stessa regola, stesso perché, di
 * `prodottiAbilitati.ts` (D3) e `membershipRoleSync.ts`.
 *
 * Modulo PURO: zero Firestore, zero I/O, zero import. Gira identico su frontend
 * (import relativo) e Cloud Functions (via `npm run sync:shared`).
 * ⚠️ L'alias `@platform-shared` punta a una cartella inesistente: non usarlo mai.
 */

// ============================================================================
// TIPI
// ============================================================================

/** Codici di WARNING (stringhe stabili: asserite dai test e loggabili). */
export const WARN_TENANT_ID_MANCANTE = 'tenant_id_mancante';
export const WARN_MEMBERSHIPS_NON_ARRAY = 'memberships_non_array';
export const WARN_MEMBERSHIP_MALFORMATA = 'membership_malformata';
export const WARN_MEMBERSHIP_DUPLICATA = 'membership_duplicata';
/** Il documento è arrivato dalla query ma non supera `appartieneAlTenant`. */
export const WARN_DERIVATO_STANTIO = 'membership_tenant_ids_stantio';

/**
 * Da dove viene il ruolo restituito da `risolviRuoloSulTenant`.
 *
 * Serve a chi logga e a chi deve decidere quanto fidarsi del dato: `top_level` è
 * legittimo solo sul tenant primario, e chi lo vede su un tenant secondario ha un
 * bug di cablatura (questo modulo non lo produce mai).
 */
export type FonteRuoloTenant =
  /** Riga `tenant_memberships[]` ATTIVA per quel tenant: la risposta precisa. */
  | 'membership'
  /** Nessuna riga attiva, ma il tenant è il PRIMARIO → `ruolo`/`roles` del documento. */
  | 'top_level'
  /** L'utente non ha alcun rapporto utilizzabile con quel tenant. */
  | 'nessuna';

/** Forma minima di una riga membership letta da questo modulo. */
export interface MembershipTenantLike {
  tenant_id: string;
  ruolo?: string;
  roles?: unknown;
  attiva?: boolean;
  [key: string]: unknown;
}

/** Forma minima del documento `app_users` letto da questo modulo. */
export interface UtenteTenantLike {
  /** Tenant PRIMARIO (rapporto di lavoro principale). */
  azienda_id?: string;
  ruolo?: string;
  roles?: unknown;
  tenant_memberships?: unknown;
  /** Campo DERIVATO scritto da `setUserClaims`: solo membership attive. */
  membership_tenant_ids?: unknown;
  [key: string]: unknown;
}

export interface RuoloSulTenantResult {
  /** Ruolo principale IN quel tenant. `undefined` se non c'è rapporto. */
  ruolo?: string;
  /** Unione di `ruolo` e `roles` IN quel tenant, dedup, ordine di apparizione. */
  roles: string[];
  fonte: FonteRuoloTenant;
  warnings: string[];
}

// ============================================================================
// HELPER INTERNE (pure)
// ============================================================================

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Id canonico per i CONFRONTI: stringa trimmata, `''` se non utilizzabile. */
function idCanonico(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Predicato di attività IDENTICO al resto del repo: `attiva !== false`
 * (assente ⇒ attiva).
 *
 * ⚠️ Volutamente NON irrigidito a `attiva === true || attiva === undefined`:
 * `prodottiAbilitati.ts:isAttiva`, `membershipRoleSync.ts` e
 * `onAppUserSeatChange.projectMembershipsForSeat` usano tutti lo stesso `!== false`.
 * Irrigidirlo QUI e basta creerebbe una divergenza nuova: un utente con
 * `attiva: null` sparirebbe dalla lista pur continuando a occupare un posto e a
 * risolvere i prodotti. Se va irrigidito, va fatto nei quattro punti insieme.
 */
function isAttiva(m: MembershipTenantLike): boolean {
  return m.attiva !== false;
}

/** Unione dedup di `ruolo` (scalare) e `roles` (array), ordine di apparizione. */
function unione(ruolo: unknown, roles: unknown): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (v: unknown) => {
    if (typeof v !== 'string') return;
    const t = v.trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    out.push(t);
  };
  push(ruolo);
  if (Array.isArray(roles)) for (const r of roles) push(r);
  return out;
}

interface RigheNormalizzate {
  righe: Array<{ riga: MembershipTenantLike; tenantId: string }>;
  warnings: string[];
}

function normalizzaMemberships(input: unknown): RigheNormalizzate {
  if (input === undefined || input === null) return { righe: [], warnings: [] };
  if (!Array.isArray(input)) return { righe: [], warnings: [WARN_MEMBERSHIPS_NON_ARRAY] };

  const righe: RigheNormalizzate['righe'] = [];
  let malformate = 0;
  for (const r of input) {
    const tenantId = isRecord(r) ? idCanonico(r.tenant_id) : '';
    if (!tenantId) {
      malformate++;
      continue;
    }
    righe.push({ riga: r as MembershipTenantLike, tenantId });
  }
  return { righe, warnings: malformate > 0 ? [WARN_MEMBERSHIP_MALFORMATA] : [] };
}

// ============================================================================
// API PUBBLICA
// ============================================================================

/** Il tenant richiesto è il tenant PRIMARIO (rapporto di lavoro principale)? */
export function isTenantPrimario(
  user: UtenteTenantLike | null | undefined,
  tenantId: string,
): boolean {
  if (!user) return false;
  const t = idCanonico(tenantId);
  return t !== '' && idCanonico(user.azienda_id) === t;
}

/**
 * La riga membership ATTIVA dell'utente su `tenantId`, se esiste.
 *
 * Fra più righe attive sullo stesso tenant (dato anomalo) vince la PRIMA: stesso
 * criterio deterministico di `prodottiAbilitati.risolviProdottiAbilitati`, così i
 * due moduli non possono scegliere righe diverse per lo stesso utente.
 */
export function membershipAttivaSulTenant(
  user: UtenteTenantLike | null | undefined,
  tenantId: string,
): { riga?: MembershipTenantLike; warnings: string[] } {
  const warnings: string[] = [];
  if (!user) return { warnings };
  const t = idCanonico(tenantId);
  if (!t) {
    warnings.push(WARN_TENANT_ID_MANCANTE);
    return { warnings };
  }
  const { righe, warnings: wm } = normalizzaMemberships(user.tenant_memberships);
  warnings.push(...wm);
  const attive = righe.filter((r) => r.tenantId === t && isAttiva(r.riga));
  if (attive.length > 1) warnings.push(WARN_MEMBERSHIP_DUPLICATA);
  return { riga: attive[0]?.riga, warnings };
}

/**
 * L'utente OPERA per `tenantId`? È il criterio che sostituisce
 * `azienda_id == tenantId` in ogni elenco di persone di un'azienda.
 *
 * Vero se:
 *  - `tenantId` è il suo tenant PRIMARIO — sempre, anche se la riga membership su
 *    quel tenant fosse sospesa. Motivo: sono i **6 utenti reali** `disabilitato`
 *    con la propria membership primaria a `attiva: false`. Restano dipendenti di
 *    quell'azienda, oggi compaiono in lista con lo stato «Disabilitato» e l'admin
 *    li deve poter riattivare. Escluderli sarebbe una regressione, non un
 *    irrigidimento; e non è un fail-open, perché lo STATO dell'utente
 *    (`status`/`active`) resta il gate operativo, qui invariato.
 *  - oppure ha una membership ATTIVA su `tenantId` (collaboratore).
 *
 * Una membership SOSPESA su un tenant NON primario è invece un rapporto chiuso:
 * l'utente NON appartiene a quel tenant. Coerente con `membership_tenant_ids`
 * (che le membership sospese non le contiene) e con il conteggio dei posti
 * (`onAppUserSeatChange.userOccupiesSeatOnTenant`, stesso identico predicato).
 */
export function appartieneAlTenant(
  user: UtenteTenantLike | null | undefined,
  tenantId: string,
): boolean {
  if (!user) return false;
  const t = idCanonico(tenantId);
  if (!t) return false;
  if (idCanonico(user.azienda_id) === t) return true;
  return membershipAttivaSulTenant(user, t).riga !== undefined;
}

/**
 * L'utente è un COLLABORATORE su questo tenant, cioè opera qui ma il suo rapporto
 * di lavoro principale è con un'ALTRA azienda?
 *
 * ⚠️ Non serve a etichettarlo in interfaccia — anzi: la decisione del proprietario
 * è che compaia come un utente NORMALE («Andrea Atzeni dovrebbe apparire come un
 * normale utente in Vivisolar e come un normale utente in Fortezza»). Serve a
 * decidere COSA NASCONDERE: tutto ciò che appartiene all'altra azienda (fascicolo
 * HR, credenziali, provvigioni, gerarchia) non va mostrato all'admin di questa.
 */
export function isCollaboratoreDiAltraAzienda(
  user: UtenteTenantLike | null | undefined,
  tenantId: string,
): boolean {
  return appartieneAlTenant(user, tenantId) && !isTenantPrimario(user, tenantId);
}

/**
 * Che RUOLO ha l'utente IN `tenantId`. È il dato da mostrare in ogni lista o
 * scheda mostrata nel contesto di quel tenant — mai `user.ruolo` grezzo.
 *
 * Ordine di risoluzione:
 *   1. riga membership ATTIVA su quel tenant                 → `membership`
 *   2. nessuna riga attiva MA il tenant è il PRIMARIO        → `top_level`
 *   3. altrimenti                                            → `nessuna` (nessun ruolo)
 *
 * Il passo 2 non è una scorciatoia ed è vincolato al tenant primario per due
 * motivi opposti e complementari:
 *  - senza di esso i **6 utenti reali** con la sola membership primaria sospesa
 *    perderebbero il ruolo in lista (oggi lo mostrano): regressione visibile;
 *  - estendendolo ai tenant secondari, `gerolamo.grosso@powerservicesrl.eu`
 *    (top-level `admin` in Power Service) comparirebbe come **Admin** nella lista
 *    di Vivisolar, dove è `tecnico`: esattamente l'escalation cross-tenant che D3
 *    vieta, per giunta mostrata a un'azienda che non c'entra.
 */
export function risolviRuoloSulTenant(
  user: UtenteTenantLike | null | undefined,
  tenantId: string,
): RuoloSulTenantResult {
  const warnings: string[] = [];
  if (!user) return { roles: [], fonte: 'nessuna', warnings };

  const t = idCanonico(tenantId);
  if (!t) {
    warnings.push(WARN_TENANT_ID_MANCANTE);
    return { roles: [], fonte: 'nessuna', warnings };
  }

  const { riga, warnings: wm } = membershipAttivaSulTenant(user, t);
  warnings.push(...wm);

  if (riga) {
    const roles = unione(riga.ruolo, riga.roles);
    return { ruolo: idCanonico(riga.ruolo) || roles[0], roles, fonte: 'membership', warnings };
  }

  if (isTenantPrimario(user, t)) {
    const roles = unione(user.ruolo, user.roles);
    return { ruolo: idCanonico(user.ruolo) || roles[0], roles, fonte: 'top_level', warnings };
  }

  return { roles: [], fonte: 'nessuna', warnings };
}

export interface UnioneUtentiArgs<T> {
  /** Risultato della query `where('azienda_id','==',tenantId)`. */
  daTenantPrimario: readonly T[];
  /** Risultato della query `where('membership_tenant_ids','array-contains',tenantId)`. */
  daMembership: readonly T[];
  tenantId: string;
  /** Identità del documento (di norma `d => d.id`). Serve alla deduplica. */
  getId: (doc: T) => string;
  /** Come leggere i campi tenant dal documento. Default: il documento stesso. */
  getUtente?: (doc: T) => UtenteTenantLike | null | undefined;
}

export interface UnioneUtentiResult<T> {
  /** Unione dedup e filtrata, nell'ordine: prima i primari, poi i collaboratori. */
  utenti: T[];
  /**
   * Documenti tornati da una query ma SCARTATI da `appartieneAlTenant`: quasi
   * sempre `membership_tenant_ids` stantìo (trigger non ancora girato) su una
   * membership appena sospesa. Restituiti — non ingoiati — perché il chiamante
   * possa loggarli: uno scarto stabile nel tempo è un derivato che non si
   * riallinea, cioè un bug del trigger, e deve essere visibile.
   */
  scartati: Array<{ id: string; motivo: string }>;
}

/**
 * Fonde i risultati delle due query in un unico elenco, deduplicato e verificato.
 *
 * Perché la deduplica è per ID e non per riferimento: chi ha `azienda_id == T` E
 * una membership su T (il caso NORMALE — `membership_tenant_ids` include il tenant
 * primario quando esiste la riga corrispondente, cioè per tutti i 59 utenti reali)
 * compare in ENTRAMBE le query, come due oggetti distinti.
 *
 * L'ordine è deterministico ma NON è quello finale: chi mostra la lista ordina
 * comunque (per cognome, ecc.), perché Firestore non può ordinare fra due query.
 */
export function unisciUtentiPerTenant<T>(args: UnioneUtentiArgs<T>): UnioneUtentiResult<T> {
  const { daTenantPrimario, daMembership, tenantId, getId, getUtente } = args;
  const leggi = getUtente ?? ((d: T) => d as unknown as UtenteTenantLike);

  const utenti: T[] = [];
  const scartati: Array<{ id: string; motivo: string }> = [];
  const visti = new Set<string>();

  const t = idCanonico(tenantId);
  if (!t) return { utenti, scartati };

  for (const gruppo of [daTenantPrimario, daMembership]) {
    for (const doc of gruppo) {
      const id = getId(doc);
      if (!id || visti.has(id)) continue;
      visti.add(id);
      if (!appartieneAlTenant(leggi(doc), t)) {
        scartati.push({ id, motivo: WARN_DERIVATO_STANTIO });
        continue;
      }
      utenti.push(doc);
    }
  }

  return { utenti, scartati };
}
